import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import Swal from "sweetalert2";
import { customPackageApi, type Permission } from "../../repositories/customPackage";
import { exclusivePermissionApi } from "../../repositories/exclusivePermission";
import EditExclusivePermissionsModal from "../../components/exclusive-permissions/EditExclusivePermissionsModal";
import PageHeader from "../../components/ui/PageHeader";
import { HOME_CRUMB } from "../../components/ui/Breadcrumbs";
import DataTable, { DataTableColumn } from "../../components/ui/DataTable";
import RowActionsMenu, { RowMenuItem } from "../../components/ui/RowActionsMenu";
import Button from "../../components/ui/Button";
import { confirmDanger } from "../../utils/swal";

// A user row from the (not-yet-built) "users with exclusive permissions" list.
interface ExclusiveUser {
  userId: number;
  fullName?: string;
  email?: string;
}

// Defensively pull the user list out of the stubbed response. Swap the mapping
// when the real endpoint lands.
const extractUsers = (data: any): ExclusiveUser[] => {
  const raw = data?.data?.users || data?.data || data?.users || (Array.isArray(data) ? data : []);
  if (!Array.isArray(raw)) return [];
  return raw.map((u: any) => ({
    userId: u.userId ?? u.user_id ?? u.id,
    fullName: u.fullName ?? u.name ?? u.full_name,
    email: u.email,
  }));
};

const ExclusivePermissions = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<ExclusiveUser[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingUser, setEditingUser] = useState<ExclusiveUser | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await exclusivePermissionApi.getUsersWithExclusivePermissions();
      setUsers(extractUsers(response.data));
    } catch (error: any) {
      // The list endpoint may not exist yet — show an empty state rather than
      // an error toast for a 404.
      if (error?.response?.status !== 404) {
        console.error("Failed to fetch users with exclusive permissions:", error);
      }
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Permissions power both the per-row summary and the Edit modal.
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const response = await customPackageApi.getAllPermissions();
        let permissionsData = [];
        if (response.data?.success && response.data?.data) {
          permissionsData = response.data.data;
        } else if (Array.isArray(response.data)) {
          permissionsData = response.data;
        }
        setPermissions(permissionsData);
      } catch (error) {
        console.error("Failed to fetch permissions:", error);
      }
    };
    fetchPermissions();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleDelete = async (user: ExclusiveUser) => {
    const result = await confirmDanger({
      title: "Revoke all exclusive permissions?",
      text: `This removes every exclusive grant for ${user.email || "this user"} (they fall back to package defaults).`,
      confirmButtonText: "Revoke all",
    });
    if (!result.isConfirmed) return;

    try {
      await exclusivePermissionApi.revokeAllPermissions(user.userId);
      Swal.fire({
        icon: "success",
        title: "Revoked",
        text: "All exclusive permissions were removed",
      });
      fetchUsers();
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.response?.data?.message || "Failed to revoke permissions",
      });
    }
  };

  const columns: DataTableColumn<ExclusiveUser>[] = [
    {
      key: "index",
      header: "#",
      cell: (_user, index) => <span className="tabular-nums text-neutral-600">{index + 1}</span>,
    },
    {
      key: "name",
      header: "Name",
      cell: (user) =>
        user.fullName ? (
          <span className="font-medium whitespace-nowrap">{user.fullName}</span>
        ) : (
          <span className="text-neutral-400">—</span>
        ),
    },
    {
      key: "email",
      header: "Email",
      cell: (user) => <span className="text-neutral-600">{user.email || "—"}</span>,
    },
    {
      key: "actions",
      header: "Actions",
      align: "center",
      cell: (user) => (
        <RowActionsMenu label={`Actions for ${user.email || user.fullName || "user"}`}>
          <RowMenuItem
            icon={<PencilIcon className="w-4 h-4" />}
            onClick={() => setEditingUser(user)}
          >
            Edit
          </RowMenuItem>
          <RowMenuItem
            tone="danger"
            icon={<TrashIcon className="w-4 h-4" />}
            onClick={() => handleDelete(user)}
          >
            Delete
          </RowMenuItem>
        </RowActionsMenu>
      ),
    },
  ];

  return (
    <div className="py-2 sm:p-6">
      <PageHeader
        breadcrumbs={[HOME_CRUMB, { label: "Exclusive Permissions" }]}
        className="mb-6"
        title="Exclusive Permissions"
        description="Grant extra permissions to specific subscribed users on top of their package."
        actions={
          <Button
            onClick={() => navigate("/exclusive-permissions/create")}
            className="w-full sm:w-auto"
          >
            Create
          </Button>
        }
      />

      <DataTable
        columns={columns}
        rows={users}
        getRowKey={(user) => user.userId}
        loading={isLoading}
        emptyMessage={
          <span className="block">
            <span className="block text-sm font-medium text-black">
              No users with exclusive permissions yet
            </span>
            <span className="block mt-1 text-sm text-neutral-500">
              Use Create to grant exclusive permissions to a user.
            </span>
          </span>
        }
      />

      {editingUser && (
        <EditExclusivePermissionsModal
          userId={editingUser.userId}
          userName={editingUser.fullName}
          userEmail={editingUser.email}
          permissions={permissions}
          onClose={() => setEditingUser(null)}
          onSaved={fetchUsers}
        />
      )}
    </div>
  );
};

export default ExclusivePermissions;
